import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Dices } from 'lucide-react';
import apiClient from '../../config/axios';
import { getActiveSystem } from '../../config/system';
import { actionFailureText } from '../../utils/loadFailure';

/**
 * The roll panel of a random-table entry (issue #311) — page content below the
 * detail body, not an action-row control (#287), like SimilarRail and
 * QualifyPanel beside it.
 *
 * It renders for any Family whose registry declares an attribute tagged with
 * the `outcomes` register — including one a user authored this afternoon,
 * served through the runtime registry — and shows three things: the table
 * itself (the `outcomes` attribute is `show_in_detail: false`, so this panel
 * is its one renderer), the Roll control, and a visible result history for
 * the CURRENT SESSION ONLY.
 *
 * The draw happens SERVER-SIDE (`GET /api/<segment>/:slug/roll`), resolved by
 * Pillar and through the reader's stack, so a homebrew's patched table rolls
 * patched — the panel never re-implements the weighting. A rolled outcome's
 * resolved refs arrive as `{ family, slug, name, entity_type }` and render as
 * links; an unresolved ref is simply not a link (the link-integrity rule).
 *
 * History persistence is `sessionStorage`, per tab and keyed per
 * (system, segment, slug) — the trackerSession precedent (#335): navigating
 * away and back keeps the evening's rolls, closing the tab forgets them.
 * Persisting beyond the session is out of scope on the issue, on purpose.
 *
 * The DM screen's tile (issue 631) is a SECOND SURFACE over the same roll:
 * `RollTableTile` below renders the control and the result without the
 * outcomes table, through the same `useRollTable` hook and the same history
 * key — a table rolled on the dashboard shows that roll on its own page, and
 * the other way round, for the rest of the session.
 */

const HISTORY_CAP = 20;

const historyKey = (segment, slug) => `rollTableHistory:${getActiveSystem()}:${segment}:${slug}`;

function readHistory(key) {
  try {
    const raw = sessionStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.slice(0, HISTORY_CAP) : [];
  } catch {
    return [];
  }
}

function writeHistory(key, rolls) {
  try {
    sessionStorage.setItem(key, JSON.stringify(rolls.slice(0, HISTORY_CAP)));
  } catch {
    // Storage refused (private mode, quota) — the in-memory list still renders.
  }
}

function OutcomeText({ text, refs, nav }) {
  const linked = (refs || []).filter((r) => r.name && r.entity_type && r.slug);
  return (
    <span>
      {text}
      {linked.length > 0 && (
        <span>
          {' — '}
          {linked.map((ref, i) => (
            <React.Fragment key={`${ref.family}-${ref.slug}`}>
              {i > 0 && ', '}
              <Link
                to={`/${ref.entity_type}?slug=${encodeURIComponent(ref.slug)}`}
                state={nav}
                className="footer-link"
              >
                {ref.name}
              </Link>
            </React.Fragment>
          ))}
        </span>
      )}
    </span>
  );
}

/**
 * The roll and its session history for one table — the one statement of how
 * a roll is asked for and remembered, shared by the detail panel and the
 * dashboard tile.
 */
export function useRollTable(segment, slug) {
  const key = historyKey(segment, slug);
  const [history, setHistory] = useState(() => readHistory(key));
  const [error, setError] = useState(null);
  const [rolling, setRolling] = useState(false);

  // A navigation from one table straight to another remounts nothing, so the
  // history must follow the key.
  useEffect(() => {
    setHistory(readHistory(key));
    setError(null);
  }, [key]);

  const roll = useCallback(() => {
    setRolling(true);
    setError(null);
    apiClient
      .get(`/api/${segment}/${encodeURIComponent(slug)}/roll`)
      .then((res) => {
        const rolled = res.data?.roll;
        if (!rolled) return;
        setHistory((prev) => {
          const next = [
            { at: Date.now(), index: rolled.index, text: rolled.text, refs: rolled.refs || [] },
            ...prev,
          ].slice(0, HISTORY_CAP);
          writeHistory(key, next);
          return next;
        });
      })
      .catch((err) => {
        const body = err.response?.data;
        // A 5xx's "Database error" or no answer at all is said in words; a refusal keeps the server's own reason.
        setError(body?.reason || actionFailureText(err) || body?.error || 'The roll failed. Try again.');
      })
      .finally(() => setRolling(false));
  }, [segment, slug, key]);

  // A fresh start for a new scene (issue 1274).
  const clear = useCallback(() => {
    setHistory([]);
    writeHistory(key, []);
  }, [key]);

  return { history, roll, rolling, error, clear };
}

const weightOf = (outcome) => (typeof outcome.weight === 'number' ? outcome.weight : 1);

/**
 * A weight as a share of the table, for the Chance column (issue 1273).
 *
 * Under 10% it keeps one decimal: on a 40-row table each row is 2.5%, and a
 * whole-number "3%" forty times over adds up to 120%. Larger shares stay whole
 * ("33%"), and a trailing ".0" is dropped ("5%").
 */
export function chanceText(weight, total) {
  if (!(total > 0) || !(weight > 0)) return '0%';
  const pct = (weight / total) * 100;
  if (pct < 1) return '<1%';
  if (pct < 10) return `${Number(pct.toFixed(1))}%`;
  return `${Math.round(pct)}%`;
}

const TILE_HISTORY_ROWS = 3;

/**
 * The dashboard's roll-in-place control (issue 631): Roll, the latest result,
 * and the last few earlier rolls — no outcomes table, which is the detail
 * page's. Same hook, same session history as the panel.
 */
export function RollTableTile({ segment, slug, from }) {
  const { history, roll, rolling, error } = useRollTable(segment, slug);
  const nav = { from };
  const [latest, ...earlier] = history;
  const muted = { color: 'var(--text-muted)' };
  return (
    <div className="mt-3" data-testid="roll-table-tile">
      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          className="btn btn-primary"
          onClick={roll}
          disabled={rolling}
          data-testid="roll-table-button"
        >
          {rolling ? 'Rolling…' : 'Roll'}
        </button>
        {error && (
          <span role="alert" className="text-sm" style={muted}>
            This table cannot be rolled ({error}).
          </span>
        )}
      </div>
      {latest && (
        <p className="mt-2 text-sm" data-testid="roll-table-result" aria-live="polite">
          <OutcomeText text={latest.text} refs={latest.refs} nav={nav} />
        </p>
      )}
      {earlier.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-xs" data-testid="roll-table-history">
          {earlier.slice(0, TILE_HISTORY_ROWS).map((rolled, i) => (
            <li key={`${rolled.at}-${i}`} style={muted}>
              <OutcomeText text={rolled.text} refs={rolled.refs} nav={nav} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function RollTablePanel({ segment, slug, outcomes, from, listFrom }) {
  const { history, roll, rolling, error, clear } = useRollTable(segment, slug);
  // The rolled row scrolls into view after a roll here, not on a restored history.
  const rolledRow = useRef(null);
  const lastAt = useRef(history[0] ? history[0].at : null);
  const latestAt = history[0] ? history[0].at : null;
  useEffect(() => {
    if (latestAt === lastAt.current) return;
    lastAt.current = latestAt;
    if (rolledRow.current && rolledRow.current.scrollIntoView) rolledRow.current.scrollIntoView({ block: 'nearest' });
  }, [latestAt]);

  const rows = Array.isArray(outcomes) ? outcomes.filter((o) => o && typeof o === 'object') : [];
  if (!rows.length) return null;

  const nav = { from, listFrom };
  const [latest, ...earlier] = history;
  const muted = { color: 'var(--text-muted)' };
  const total = rows.reduce((sum, o) => sum + weightOf(o), 0);

  return (
    <div className="card" data-testid="roll-table-panel">
      <h2 className="section-header flex items-center gap-2">
        <Dices className="w-5 h-5" style={{ color: '#C9A84C' }} aria-hidden="true" />
        <span>Roll on This Table</span>
      </h2>

      <div className="overflow-x-auto mt-2">
        <table className="w-full text-sm" data-testid="roll-table-outcomes">
          <thead>
            <tr className="text-left">
              <th className="pr-4 py-1 font-semibold">Weight</th>
              <th className="pr-4 py-1 font-semibold">Chance</th>
              <th className="py-1 font-semibold">Result</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((outcome, i) => {
              // The rolled row stands out: accent background and a marker (issue 1274).
              const rolled = latest && latest.index === i;
              return (
                <tr
                  key={i}
                  ref={rolled ? rolledRow : undefined}
                  className={rolled ? 'font-semibold' : undefined}
                  style={rolled ? { background: 'rgba(201, 168, 76, 0.2)' } : undefined}
                  aria-current={rolled ? 'true' : undefined}
                  data-testid={rolled ? 'roll-table-rolled-row' : undefined}
                >
                  <td className="pr-4 py-1 align-top">{weightOf(outcome)}</td>
                  <td className="pr-4 py-1 align-top">{chanceText(weightOf(outcome), total)}</td>
                  <td className="py-1">
                    {rolled && <Dices className="w-4 h-4 inline mr-1" style={{ color: '#C9A84C' }} aria-label="Rolled" />}
                    {typeof outcome.text === 'string' ? outcome.text : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="text-xs mt-1" style={muted} data-testid="roll-table-total">Total weight {total}.</p>
      </div>

      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <button
          type="button"
          className="btn btn-primary"
          onClick={roll}
          disabled={rolling}
          data-testid="roll-table-button"
        >
          {rolling ? 'Rolling…' : 'Roll'}
        </button>
        {error && (
          <span role="alert" className="text-sm" style={muted}>
            This table cannot be rolled ({error}).
          </span>
        )}
      </div>

      {latest && (
        <div className="mt-3" data-testid="roll-table-result" aria-live="polite">
          <h3 className="text-sm font-semibold">Result (roll {history.length})</h3>
          <p className="mt-1">
            <OutcomeText text={latest.text} refs={latest.refs} nav={nav} />
          </p>
        </div>
      )}

      {earlier.length > 0 && (
        <div className="mt-3" data-testid="roll-table-history">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">Earlier this session</h3>
            <button type="button" className="btn btn-ghost text-xs px-1" onClick={clear}>Clear</button>
          </div>
          <ul className="mt-1 space-y-0.5 text-sm">
            {earlier.map((rolled, i) => (
              <li key={`${rolled.at}-${i}`} style={muted}>
                <span className="mr-1">{history.length - 1 - i}.</span>
                <OutcomeText text={rolled.text} refs={rolled.refs} nav={nav} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
